import React, { useState, useEffect, useCallback } from 'react';
import { Select, Form, Input, Typography, message } from 'antd';
import PropTypes from 'prop-types';
import api from '../../services/api'; // Assuming api service is correctly set up

const MetricCodeSelector = ({ metricId, metricCodeList = [], value, variables, onChange }) => {
    const [selectedCode, setSelectedCode] = useState(value);
    const [currentVariables, setCurrentVariables] = useState(variables || {});
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Update internal state when props change
    useEffect(() => {
        setSelectedCode(value);
        // If code exists, ensure variables are loaded (or use passed ones)
        const codeDetails = metricCodeList.find(c => c.code === value);
        const initialVars = variables || codeDetails?.variables || {};
        setCurrentVariables(initialVars);

    }, [value, variables, metricCodeList]);

    const handleCodeChange = useCallback((code) => {
        const selectedCodeData = metricCodeList.find(c => c.code === code);
        setSelectedCode(code);

        // If a code is selected, use its default variables, otherwise clear
        const newVariables = selectedCodeData ? (selectedCodeData.variables || []) : [];
        const varsObject = {};
        if (Array.isArray(newVariables)) {
            newVariables.forEach(v => { varsObject[v.name] = ''; }); // Initialize with empty strings
        }

        setCurrentVariables(varsObject);
        // Notify parent: pass null if code is cleared
        onChange(metricId, code ? { code: code, variables: varsObject } : null);

    }, [metricId, metricCodeList, onChange]);

    const handleVariableChange = useCallback((varName, varValue) => {
        const updatedVariables = { ...currentVariables, [varName]: varValue };
        setCurrentVariables(updatedVariables);
        // Notify parent immediately with updated variables
        onChange(metricId, selectedCode ? { code: selectedCode, variables: updatedVariables } : null);
    }, [metricId, selectedCode, currentVariables, onChange]);

    const codeDetails = metricCodeList.find(c => c.code === selectedCode);
    const displayVariables = Array.isArray(codeDetails?.variables) ? codeDetails.variables : [];

    return (
        <div>
            <Form.Item label="Metrik Kodu" style={{ marginBottom: '10px' }}>
                <Select
                    showSearch
                    allowClear
                    placeholder="Metrik Kodu Seçin veya Boş Bırakın"
                    value={selectedCode}
                    onChange={handleCodeChange}
                    style={{ width: '100%' }}
                    filterOption={(input, option) =>
                        option.children.toLowerCase().includes(input.toLowerCase())
                    }
                    loading={loadingDetails} // You might want to add loading state if fetching codes dynamically
                >
                    {metricCodeList.map(code => (
                        <Select.Option key={code.id} value={code.code}>{code.code} {code.description ? `- ${code.description}` : ''}</Select.Option>
                    ))}
                </Select>
            </Form.Item>

            {/* Only show variables section if a code is selected and it has variables */} 
            {codeDetails && displayVariables.length > 0 && (
                <div style={{ marginTop: '10px', padding: '10px', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
                    <Typography.Text strong>Kod Değişkenleri:</Typography.Text>
                    {displayVariables.map(variable => (
                        <Form.Item 
                            key={variable.name} 
                            label={variable.name} 
                            style={{ marginBottom: '5px' }}
                            tooltip={variable.description} // Show description as tooltip
                        >
                            <Input
                                value={currentVariables[variable.name] || ''}
                                onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                                placeholder={`Değer girin (${variable.description || 'açıklama yok'})`}
                            />
                        </Form.Item>
                    ))}
                </div>
            )}
        </div>
    );
};

MetricCodeSelector.propTypes = {
    metricId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    metricCodeList: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        code: PropTypes.string.isRequired,
        description: PropTypes.string,
        variables: PropTypes.arrayOf(PropTypes.shape({
            name: PropTypes.string.isRequired,
            description: PropTypes.string
        }))
    })),
    value: PropTypes.string, // The selected code string
    variables: PropTypes.object, // The current variables object {varName: value}
    onChange: PropTypes.func.isRequired, // Callback: (metricId, { code, variables } | null)
};

export default MetricCodeSelector; 